import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Compass } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DirShell, H } from "@/components/directory/DirShell";
import { PostCard } from "@/components/directory/PostCard";
import { WelcomeCard } from "@/components/directory/Welcome";
import { Logo } from "@/components/directory/ShopCard";

const FollowingRail = ({ shops }) => (
  <aside className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3 h-fit lg:sticky lg:top-36" data-testid="feed-following">
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Shops you follow</p>
    {shops.map(s => <Link key={s.id} to={`/directory/shop/${s.slug}`} className="flex items-center gap-2.5 hover:bg-slate-50 rounded-lg p-1 -m-1" data-testid={`feed-following-${s.slug}`}><Logo s={s} className="w-9 h-9 text-sm" /><span className="min-w-0"><span className="block text-sm font-semibold text-slate-900 truncate">{s.name}</span><span className="block text-xs text-slate-500">{[s.city, s.state].filter(Boolean).join(", ")}</span></span></Link>)}
    {!shops.length && <p className="text-sm text-slate-500">You're not following any shops yet.</p>}
    <Link to="/directory" className="text-sm font-bold text-blue-600 flex items-center gap-1" data-testid="feed-find-shops"><Compass className="w-4 h-4" />Find shops to follow</Link>
  </aside>
);

export default function DirectoryFeed() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [shops, setShops] = useState([]);
  useEffect(() => {
    api.get("/api/dir/feed").then(r => setD(r.data)).catch(() => setD({ items: [], following: 0 }));
    if (user) api.get("/api/dir/following").then(r => setShops(r.data)).catch(() => {});
  }, [user]);
  return (
    <DirShell>
      <div className="max-w-6xl mx-auto px-4 py-10">
        <H className="text-slate-900 !text-4xl sm:!text-5xl">My feed</H>
        <p className="mt-2 text-slate-600" data-testid="feed-subtitle">{d?.following ? "The latest from the shops you follow." : "Follow shops to fill your feed. Here's what's new across the directory."}</p>
        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4" data-testid="feed-posts">
            <WelcomeCard />
            {!d && <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
            {d?.items.map(p => <PostCard key={p.id} p={p} onRemoved={(id) => setD({ ...d, items: d.items.filter(x => x.id !== id) })} />)}
            {d && !d.items.length && <p className="text-center py-16 bg-white border border-dashed rounded-2xl text-slate-500" data-testid="feed-empty">No posts yet.</p>}
          </div>
          {user ? <FollowingRail shops={shops} /> : <aside className="bg-slate-900 text-white rounded-2xl p-5 h-fit" data-testid="feed-guest"><p className="font-bold">Sign in to build your feed</p><p className="text-sm text-slate-300 mt-1">Follow shops near you and see their photos, videos and specials here.</p></aside>}
        </div>
      </div>
    </DirShell>
  );
}
